import React, { useState, useRef } from 'react';
import {
    Box, Container, Typography, Paper, TextField, MenuItem, Button,
    Stack, Alert, CircularProgress, Divider, Checkbox, FormControlLabel,
    IconButton, FormGroup,
} from '@mui/material';
import ArrowBackIcon        from '@mui/icons-material/ArrowBack';
import DirectionsBikeIcon  from '@mui/icons-material/DirectionsBike';
import AddPhotoAlternateIcon from '@mui/icons-material/AddPhotoAlternate';
import AddIcon              from '@mui/icons-material/Add';
import DeleteIcon           from '@mui/icons-material/Delete';
import { useNavigate }     from 'react-router-dom';
import { useTranslation }  from 'react-i18next';
import axios               from '../api/axiosConfig';
import { useAuth }         from '../context/AuthContext';

const TERRACOTTA = '#C4622D';
const WALNUT     = '#2C1F14';
const FOREST     = '#4A6741';
const PARCHMENT  = '#F7F3EE';

const CURRENCIES   = ['EUR', 'USD', 'AMD', 'GBP', 'RUB', 'JPY', 'CNY', 'AUD', 'CAD', 'CHF'];
const DIFFICULTIES = ['EASY', 'MEDIUM', 'HARD'];

const MATERIALS = [
    { key: 'bolsaManillar',     label: 'Bolsa de manillar' },
    { key: 'gps',               label: 'GPS (para descargar)' },
    { key: 'soporteBotella',    label: 'Soporte botella de agua' },
    { key: 'transportinAlforja', label: 'Transportín y una alforja' },
    { key: 'kitHerramientas',   label: 'Kit de herramientas por habitación' },
];

function SectionHeader({ children }) {
    return (
        <Typography variant="caption" fontWeight={700}
            sx={{ color: WALNUT, textTransform: 'uppercase', letterSpacing: '0.08em',
                fontSize: '0.72rem', display: 'block', mb: 1.5, mt: 0.5 }}>
            {children}
        </Typography>
    );
}

function ImagePicker({ preview, onPick, label, height = 160 }) {
    const ref = useRef(null);
    return (
        <Box>
            <Box onClick={() => ref.current?.click()}
                sx={{ height, borderRadius: 2, border: `2px dashed rgba(196,98,45,0.35)`,
                    cursor: 'pointer', overflow: 'hidden',
                    bgcolor: preview ? 'transparent' : 'rgba(196,98,45,0.04)',
                    backgroundImage: preview ? `url(${preview})` : 'none',
                    backgroundSize: 'cover', backgroundPosition: 'center',
                    display: 'flex', alignItems: 'center', justifyContent: 'center',
                    '&:hover': { borderColor: TERRACOTTA } }}>
                <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 0.5,
                    bgcolor: preview ? 'rgba(0,0,0,0.4)' : 'transparent', px: 2, py: 1, borderRadius: 2 }}>
                    <AddPhotoAlternateIcon sx={{ color: preview ? 'white' : TERRACOTTA, fontSize: '2rem' }} />
                    <Typography variant="caption" fontWeight={600} sx={{ color: preview ? 'white' : TERRACOTTA }}>
                        {preview ? 'Change Image' : label}
                    </Typography>
                </Box>
            </Box>
            <input ref={ref} type="file" hidden accept="image/*"
                onChange={e => { const f = e.target.files[0]; if (f) onPick(f); }} />
        </Box>
    );
}

export default function CreateBikeTravelOffer() {
    const { t }    = useTranslation();
    const navigate = useNavigate();
    const { user, loading: authLoading } = useAuth();

    const isAdmin   = user?.role === 'ADMIN';
    const isManager = user?.role === 'MANAGER';

    const [form, setForm] = useState({
        title:       '',
        description: '',
        currency:    'EUR',
        basePrice:   '',
        difficulty:  'MEDIUM',
        distance:    '',
        duration:    '',
        region:      '',
        included:    '',
        excluded:    '',
        bolsaManillar:     false,
        gps:               false,
        soporteBotella:    false,
        transportinAlforja: false,
        kitHerramientas:   false,
    });

    const [coverFile,    setCoverFile]    = useState(null);
    const [coverPreview, setCoverPreview] = useState(null);

    // stops: [{ title, description (hotel name), imageFile, imagePreview }]
    const [stops, setStops] = useState([{ title: '', description: '', imageFile: null, imagePreview: null }]);

    // faqs: [{ question, answer }]
    const [faqs, setFaqs] = useState([{ question: '', answer: '' }]);

    const [saving, setSaving] = useState(false);
    const [error,  setError]  = useState('');

    if (authLoading) {
        return null; // still loading auth
    }
    if (!user || (!isAdmin && !isManager)) {
        navigate('/'); return null;
    }

    const set = (field, val) => setForm(f => ({ ...f, [field]: val }));

    // ── Stops helpers ────────────────────────────────────────────────────────
    const addStop = () =>
        setStops(s => [...s, { title: '', description: '', imageFile: null, imagePreview: null }]);

    const removeStop = i =>
        setStops(s => s.filter((_, idx) => idx !== i));

    const setStop = (i, field, val) =>
        setStops(s => s.map((stop, idx) => idx === i ? { ...stop, [field]: val } : stop));

    const setStopImage = (i, file) =>
        setStops(s => s.map((stop, idx) =>
            idx === i ? { ...stop, imageFile: file, imagePreview: URL.createObjectURL(file) } : stop));

    // ── FAQ helpers ──────────────────────────────────────────────────────────
    const addFaq    = () => setFaqs(f => [...f, { question: '', answer: '' }]);
    const removeFaq = i  => setFaqs(f => f.filter((_, idx) => idx !== i));
    const setFaq    = (i, field, val) =>
        setFaqs(f => f.map((faq, idx) => idx === i ? { ...faq, [field]: val } : faq));

    // ── Submit ───────────────────────────────────────────────────────────────
    const handleSubmit = async () => {
        if (!form.title.trim())  { setError('Title is required.');        return; }
        if (!form.basePrice)     { setError('Price is required.');         return; }
        if (!form.region.trim()) { setError('Region is required.');        return; }

        setSaving(true); setError('');
        try {
            // 1 — Create the offer
            const offerPayload = {
                ...form,
                basePrice: Math.round(Number(form.basePrice) * 100),
                distance:  form.distance ? Number(form.distance) : null,
                duration:  form.duration ? parseInt(form.duration, 10) : null,
            };
            const offerData = new FormData();
            offerData.append('offer', new Blob([JSON.stringify(offerPayload)], { type: 'application/json' }));
            if (coverFile) offerData.append('image', coverFile);

            const { data: created } = await axios.post(
                '/api/bike-travel-offers/offer', offerData,
                { headers: { 'Content-Type': 'multipart/form-data' } }
            );
            const offerId = created.id;

            // 2 — Create stops (sequential to preserve order)
            const validStops = stops.filter(s => s.title.trim());
            for (let i = 0; i < validStops.length; i++) {
                const s = validStops[i];
                const stopPayload = {
                    offerId,
                    type:        'TRAVEL',
                    title:       s.title,
                    description: s.description,
                    stopOrder:   i,
                };
                const stopData = new FormData();
                stopData.append('stop', new Blob([JSON.stringify(stopPayload)], { type: 'application/json' }));
                if (s.imageFile) stopData.append('image', s.imageFile);
                await axios.post('/stops/stop', stopData, { headers: { 'Content-Type': 'multipart/form-data' } });
            }

            // 3 — Create FAQs
            const validFaqs = faqs.filter(f => f.question.trim() && f.answer.trim());
            await Promise.all(validFaqs.map(f =>
                axios.post('/api/faqs', { question: f.question, answer: f.answer, offerId, offerType: 'TRAVEL' })
            ));

            navigate(`/bike-travel-offers/${offerId}`);
        } catch (err) {
            setError(err.response?.data?.message || 'Failed to create bike travel offer.');
        } finally {
            setSaving(false);
        }
    };

    // ── Shared field sx ──────────────────────────────────────────────────────
    const fieldSx = {
        '& .MuiOutlinedInput-root': {
            '&.Mui-focused .MuiOutlinedInput-notchedOutline': { borderColor: TERRACOTTA },
        },
    };

    return (
        <Box sx={{ minHeight: '100vh', bgcolor: PARCHMENT }}>
            {/* Banner */}
            <Box sx={{ height: 120,
                background: `linear-gradient(160deg, ${WALNUT} 0%, #5A3020 50%, ${FOREST} 100%)`,
                position: 'relative', overflow: 'hidden',
                '&::after': { content: '""', position: 'absolute', bottom: 0, left: 0, right: 0,
                    height: 40, background: PARCHMENT, borderRadius: '40px 40px 0 0' } }} />

            <Container maxWidth="sm" sx={{ mt: -3, pb: 8, position: 'relative', zIndex: 1 }}>
                {/* Back */}
                <Stack direction="row" alignItems="center" sx={{ mb: 3 }}>
                    <Button startIcon={<ArrowBackIcon />} onClick={() => navigate(-1)}
                        sx={{ color: TERRACOTTA, fontWeight: 600 }}>
                        {t('common.back', 'Back')}
                    </Button>
                </Stack>

                <Paper elevation={0} sx={{ p: 4, borderRadius: 3,
                    border: '1px solid rgba(196,98,45,0.1)',
                    boxShadow: '0 2px 20px rgba(44,31,20,0.07)' }}>

                    {/* Header */}
                    <Stack direction="row" spacing={1.5} alignItems="center" sx={{ mb: 3 }}>
                        <Box sx={{ width: 44, height: 44, borderRadius: 2,
                            bgcolor: 'rgba(196,98,45,0.1)',
                            display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                            <DirectionsBikeIcon sx={{ color: TERRACOTTA }} />
                        </Box>
                        <Box>
                            <Typography variant="h5" fontWeight={800}
                                sx={{ color: WALNUT, fontFamily: "'Playfair Display', Georgia, serif" }}>
                                Create Bike Travel Offer
                            </Typography>
                            <Typography variant="body2" color="text.secondary">
                                Set up a new cycling travel package
                            </Typography>
                        </Box>
                    </Stack>

                    <Divider sx={{ mb: 3 }} />

                    <Stack spacing={2.5}>

                        {/* ── Cover image ──────────────────────────────────── */}
                        <Box>
                            <SectionHeader>Cover Image</SectionHeader>
                            <ImagePicker preview={coverPreview} label="Upload Cover Image"
                                onPick={f => { setCoverFile(f); setCoverPreview(URL.createObjectURL(f)); }} />
                        </Box>

                        <Divider />

                        {/* ── Basic info ───────────────────────────────────── */}
                        <SectionHeader>Basic Info</SectionHeader>

                        <TextField fullWidth label="Title *" value={form.title}
                            onChange={e => set('title', e.target.value)}
                            placeholder="e.g. Ardahan Mountain Circuit" sx={fieldSx} />

                        <TextField fullWidth multiline rows={3} label="Description"
                            value={form.description} onChange={e => set('description', e.target.value)}
                            sx={fieldSx} />

                        <Stack direction="row" spacing={2}>
                            <TextField fullWidth label="Price * (€)" type="number" value={form.basePrice}
                                onChange={e => set('basePrice', e.target.value)}
                                placeholder="e.g. 50 for €50.00"
                                inputProps={{ min: 0, step: '0.01' }} sx={fieldSx} />
                            <TextField select fullWidth label="Currency" value={form.currency}
                                onChange={e => set('currency', e.target.value)} sx={fieldSx}>
                                {CURRENCIES.map(c => <MenuItem key={c} value={c}>{c}</MenuItem>)}
                            </TextField>
                        </Stack>

                        <Stack direction="row" spacing={2}>
                            <TextField select fullWidth label="Difficulty" value={form.difficulty}
                                onChange={e => set('difficulty', e.target.value)} sx={fieldSx}>
                                {DIFFICULTIES.map(d => <MenuItem key={d} value={d}>{d}</MenuItem>)}
                            </TextField>
                            <TextField fullWidth label="Distance (km)" type="number"
                                value={form.distance} onChange={e => set('distance', e.target.value)}
                                inputProps={{ min: 0, step: 0.1 }} sx={fieldSx} />
                            <TextField fullWidth label="Duration (days)" type="number"
                                value={form.duration} onChange={e => set('duration', e.target.value)}
                                inputProps={{ min: 1, step: 1 }} sx={fieldSx} />
                        </Stack>

                        <TextField fullWidth label="Region *" value={form.region}
                            onChange={e => set('region', e.target.value)}
                            placeholder="e.g. Kaçkar Mountains" sx={fieldSx} />

                        <Divider />

                        {/* ── Included / Excluded ──────────────────────────── */}
                        <SectionHeader>Included &amp; Excluded</SectionHeader>

                        <TextField fullWidth multiline rows={3} label="Included"
                            value={form.included} onChange={e => set('included', e.target.value)}
                            placeholder="e.g. Guide, accommodation, bike rental…" sx={fieldSx} />

                        <TextField fullWidth multiline rows={3} label="Excluded"
                            value={form.excluded} onChange={e => set('excluded', e.target.value)}
                            placeholder="e.g. Flights, personal expenses…" sx={fieldSx} />

                        <Divider />

                        {/* ── Materials ────────────────────────────────────── */}
                        <SectionHeader>Materials Included</SectionHeader>
                        <FormGroup>
                            {MATERIALS.map(m => (
                                <FormControlLabel key={m.key}
                                    control={
                                        <Checkbox checked={form[m.key]}
                                            onChange={e => set(m.key, e.target.checked)}
                                            sx={{ color: TERRACOTTA,
                                                '&.Mui-checked': { color: TERRACOTTA } }} />
                                    }
                                    label={<Typography variant="body2">{m.label}</Typography>}
                                />
                            ))}
                        </FormGroup>

                        <Divider />

                        {/* ── Itinerary stops ──────────────────────────────── */}
                        <Box>
                            <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 1.5 }}>
                                <SectionHeader>Itinerary Stops</SectionHeader>
                                <Button size="small" startIcon={<AddIcon />} onClick={addStop}
                                    sx={{ color: TERRACOTTA, fontWeight: 700, fontSize: '0.78rem',
                                        '&:hover': { bgcolor: 'rgba(196,98,45,0.06)' } }}>
                                    Add Stop
                                </Button>
                            </Stack>

                            <Stack spacing={2}>
                                {stops.map((stop, i) => (
                                    <Paper key={i} elevation={0} sx={{ p: 2, borderRadius: 2,
                                        border: '1px solid rgba(196,98,45,0.15)',
                                        bgcolor: 'rgba(196,98,45,0.02)' }}>
                                        <Stack direction="row" justifyContent="space-between"
                                            alignItems="center" sx={{ mb: 1.5 }}>
                                            <Typography variant="caption" fontWeight={700}
                                                sx={{ color: WALNUT }}>
                                                Stop {i + 1}
                                            </Typography>
                                            {stops.length > 1 && (
                                                <IconButton size="small" onClick={() => removeStop(i)}
                                                    sx={{ color: 'text.secondary' }}>
                                                    <DeleteIcon fontSize="small" />
                                                </IconButton>
                                            )}
                                        </Stack>
                                        <Stack spacing={1.5}>
                                            <TextField fullWidth size="small" label="Stop name"
                                                value={stop.title}
                                                onChange={e => setStop(i, 'title', e.target.value)}
                                                sx={fieldSx} />
                                            <TextField fullWidth size="small" label="Hotel name (optional)"
                                                value={stop.description}
                                                onChange={e => setStop(i, 'description', e.target.value)}
                                                placeholder="e.g. Hotel Kaçkar" sx={fieldSx} />
                                            <ImagePicker preview={stop.imagePreview}
                                                label="Upload Stop Image" height={110}
                                                onPick={f => setStopImage(i, f)} />
                                        </Stack>
                                    </Paper>
                                ))}
                            </Stack>
                        </Box>

                        <Divider />

                        {/* ── FAQs ─────────────────────────────────────────── */}
                        <Box>
                            <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 1.5 }}>
                                <SectionHeader>FAQs</SectionHeader>
                                <Button size="small" startIcon={<AddIcon />} onClick={addFaq}
                                    sx={{ color: TERRACOTTA, fontWeight: 700, fontSize: '0.78rem',
                                        '&:hover': { bgcolor: 'rgba(196,98,45,0.06)' } }}>
                                    Add FAQ
                                </Button>
                            </Stack>

                            <Stack spacing={2}>
                                {faqs.map((faq, i) => (
                                    <Paper key={i} elevation={0} sx={{ p: 2, borderRadius: 2,
                                        border: '1px solid rgba(196,98,45,0.15)',
                                        bgcolor: 'rgba(196,98,45,0.02)' }}>
                                        <Stack direction="row" justifyContent="space-between"
                                            alignItems="center" sx={{ mb: 1.5 }}>
                                            <Typography variant="caption" fontWeight={700}
                                                sx={{ color: WALNUT }}>
                                                FAQ {i + 1}
                                            </Typography>
                                            {faqs.length > 1 && (
                                                <IconButton size="small" onClick={() => removeFaq(i)}
                                                    sx={{ color: 'text.secondary' }}>
                                                    <DeleteIcon fontSize="small" />
                                                </IconButton>
                                            )}
                                        </Stack>
                                        <Stack spacing={1.5}>
                                            <TextField fullWidth size="small" label="Question"
                                                value={faq.question}
                                                onChange={e => setFaq(i, 'question', e.target.value)}
                                                sx={fieldSx} />
                                            <TextField fullWidth multiline rows={2} size="small" label="Answer"
                                                value={faq.answer}
                                                onChange={e => setFaq(i, 'answer', e.target.value)}
                                                sx={fieldSx} />
                                        </Stack>
                                    </Paper>
                                ))}
                            </Stack>
                        </Box>

                        {error && <Alert severity="error">{error}</Alert>}

                        <Stack direction="row" spacing={2} pt={1}>
                            <Button fullWidth variant="outlined" onClick={() => navigate(-1)}
                                disabled={saving}
                                sx={{ borderColor: 'rgba(196,98,45,0.4)', color: TERRACOTTA,
                                    borderRadius: 2, fontWeight: 700 }}>
                                {t('common.cancel', 'Cancel')}
                            </Button>
                            <Button fullWidth variant="contained" onClick={handleSubmit}
                                disabled={saving}
                                sx={{ bgcolor: TERRACOTTA, '&:hover': { bgcolor: '#9A4A1F' },
                                    borderRadius: 2, fontWeight: 700 }}>
                                {saving
                                    ? <CircularProgress size={20} sx={{ color: 'white' }} />
                                    : 'Create Offer'}
                            </Button>
                        </Stack>
                    </Stack>
                </Paper>
            </Container>
        </Box>
    );
}
